import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { AlertCircle, Link2, Plus } from 'lucide-react'
import type { Organisation } from '../../../types'

interface SelectorProps {
  organisations: Organisation[]
  selectedOrgId: string
  isLoadingOrgs: boolean
  onOrgChange: (orgId: string) => void
  t: TFunction<'admin'>
}

export function OrganisationSelect({
  organisations,
  selectedOrgId,
  isLoadingOrgs,
  onOrgChange,
  t,
}: SelectorProps): ReactElement {
  return (
    <div className="flex-1">
      <label
        htmlFor="organisationId"
        className="mb-1 block text-sm font-medium text-stone-600 dark:text-stone-400"
      >
        {t('licensing.xero.selectOrganisation')}
      </label>
      <select
        id="organisationId"
        value={selectedOrgId}
        onChange={(e): void => {
          onOrgChange(e.target.value)
        }}
        disabled={isLoadingOrgs}
        className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:border-[#4e5e82] focus:ring-1 focus:ring-[#4e5e82] focus:outline-none disabled:opacity-50 dark:border-stone-600 dark:bg-stone-700 dark:text-white"
      >
        <option value="">
          {isLoadingOrgs
            ? t('licensing.xero.loadingOrgs')
            : t('licensing.xero.selectOrgPlaceholder')}
        </option>
        {organisations.map((org) => (
          <option key={org.id} value={org.id}>
            {org.name} {org.xeroOrganisationId && t('licensing.xeroLinked')}
          </option>
        ))}
      </select>
    </div>
  )
}

interface NewOrgButtonProps {
  onClick: () => void
  t: TFunction<'admin'>
}

export function NewOrgButton({ onClick, t }: NewOrgButtonProps): ReactElement {
  return (
    <div className="flex items-end">
      <button
        type="button"
        onClick={onClick}
        className="flex items-center gap-1 rounded-lg border border-stone-300 px-3 py-2 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-50 dark:border-stone-600 dark:text-stone-300 dark:hover:bg-stone-700"
      >
        <Plus className="h-4 w-4" />
        {t('licensing.xero.newOrg')}
      </button>
    </div>
  )
}

interface OrgWarningProps {
  selectedOrg: Organisation | undefined
  selectedOrgId: string
  xeroOrganisationId: string
  t: TFunction<'admin'>
}

export function OrgStatusBanner({
  selectedOrg,
  selectedOrgId,
  xeroOrganisationId,
  t,
}: OrgWarningProps): ReactElement | null {
  if (selectedOrg && !selectedOrg.xeroOrganisationId) {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm dark:border-amber-800 dark:bg-amber-900/20">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
        <span className="text-amber-700 dark:text-amber-300">
          {t('licensing.xero.orgNoXeroIdWarning')}
        </span>
      </div>
    )
  }
  if (selectedOrg?.xeroOrganisationId) {
    return (
      <div className="flex items-center gap-2 text-sm text-[#5c6e94] dark:text-[#8a9bba]">
        <Link2 className="h-4 w-4" />
        <span>
          {t('licensing.xero.orgLinkedToXeroId')}:{' '}
          <code className="font-mono text-xs">{selectedOrg.xeroOrganisationId}</code>
        </span>
      </div>
    )
  }
  if (!selectedOrgId && xeroOrganisationId) {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm dark:border-red-800 dark:bg-red-900/20">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600 dark:text-red-400" />
        <div className="text-red-700 dark:text-red-300">
          {t('licensing.xero.noOrgSelectedWarning')}
        </div>
      </div>
    )
  }
  return null
}
